import React, { useEffect, useState } from 'react';
import axios from '../config/axios';
import { useAuth } from '../context/AuthContext';
import { usePageLoading } from '../context/PageLoadingContext';
import { useToast } from '../context/ToastContext';
import { FaPlus, FaEdit, FaImage, FaCamera, FaSpinner, FaTimes } from 'react-icons/fa';

interface Category {
  id: string;
  name: string;
  imageUrl?: string;
  active?: boolean;
  parent?: string | null;
  parentId?: string | null;
}

const Categories: React.FC = () => {
  const { user } = useAuth();
  const { setPageLoading } = usePageLoading();
  const toast = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', imageUrl: '', parentId: '' });
  const [formError, setFormError] = useState('');
  const [editForm, setEditForm] = useState<Category | null>(null);
  const [editError, setEditError] = useState('');
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [loadingToggle, setLoadingToggle] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (name: string) => setExpanded(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    setLoading(true);
    setPageLoading(true);
    try {
      const res = await axios.get('/categories');
      setCategories(res.data.categories || []);
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      await axios.post('/categories', { name: form.name, imageUrl: form.imageUrl, parentId: form.parentId || null });
      setShowForm(false);
      setForm({ name: '', imageUrl: '', parentId: '' });
      toast.success('Categoria creada');
      loadCategories();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Error al crear categoria');
    }
  };

  const handleEditClick = (cat: Category) => {
    setEditForm(cat);
    setEditError('');
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm) return;
    if (!editForm.name.trim()) { setEditError('El nombre es requerido'); return; }
    setEditError('');
    setUpdating(true);
    try {
      await axios.put(`/categories/${editForm.id}`, { name: editForm.name.trim(), imageUrl: editForm.imageUrl, parentId: editForm.parentId ?? null });
      setEditForm(null);
      loadCategories();
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Error al actualizar categoria');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleActive = async (cat: Category) => {
    setLoadingToggle(cat.id);
    try {
      await axios.put(`/categories/${cat.id}`, { active: !cat.active });
      loadCategories();
    } catch {
      toast.error('Error al cambiar el estado');
    } finally {
      setLoadingToggle(null);
    }
  };

  // Subida en formularios (crear/editar): solo obtiene la URL; se guarda al enviar el form
  const handleImageUpload = async (name: string, file: File, isEdit = false) => {
    try {
      setUploadingImage(name);
      setPageLoading(true);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', name);
      const res = await axios.post('/categories/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (isEdit && editForm) setEditForm({ ...editForm, imageUrl: res.data.imageUrl });
      else setForm(f => ({ ...f, imageUrl: res.data.imageUrl }));
      toast.success('Imagen subida');
    } catch {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImage(null);
      setPageLoading(false);
    }
  };

  // Subida rápida desde la lista: sube la imagen y la guarda de inmediato en esa categoría
  const handleRowImageUpload = async (cat: Category, file: File) => {
    try {
      setUploadingImage(cat.id);
      setPageLoading(true);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', cat.name);
      const res = await axios.post('/categories/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await axios.put(`/categories/${cat.id}`, { imageUrl: res.data.imageUrl });
      await loadCategories();
      toast.success('Imagen actualizada');
    } catch {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImage(null);
      setPageLoading(false);
    }
  };

  if (!user || user.role !== 'administrador') {
    return <div className="container-fluid"><h2 className="mt-4">Acceso restringido</h2></div>;
  }

  // Categorías raíz (sin padre) — candidatas a ser categoría padre
  const rootCategories = categories.filter(c => !c.parentId);
  const childrenOf = (id: string) => categories.filter(c => c.parentId === id);
  const hasChildren = (id: string) => categories.some(c => c.parentId === id);
  // Filas de nivel principal: raíces + huérfanas (padre inexistente) para no ocultarlas
  const topLevel = [
    ...rootCategories,
    ...categories.filter(c => c.parentId && !rootCategories.some(r => r.id === c.parentId)),
  ];

  const renderRow = (cat: Category, depth: number, childCount: number, open: boolean) => (
    <div key={cat.id} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: 12,
      marginLeft: depth ? 26 : 0,
      borderRadius: 12, border: '1px solid #e2e8f0',
      background: cat.active === false ? '#f8fafc' : depth ? '#f8faff' : '#fff',
      opacity: cat.active === false ? 0.6 : 1,
      borderLeft: cat.active === false ? '4px solid #dc3545' : depth ? '4px solid #c7d2fe' : '4px solid transparent',
    }}>
      {/* Chevron (padres con hijas) / conector (subcategoría) */}
      {childCount > 0 ? (
        <button type="button" onClick={() => toggleExpand(cat.id)} title={open ? 'Ocultar subcategorías' : 'Ver subcategorías'}
          style={{ width: 24, height: 24, flexShrink: 0, border: 'none', background: '#eef2ff', color: '#4f46e5', borderRadius: 6, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{open ? '▾' : '▸'}</button>
      ) : depth ? (
        <span style={{ width: 24, flexShrink: 0, textAlign: 'center', color: '#c7d2fe' }}>└</span>
      ) : (
        <span style={{ width: 24, flexShrink: 0 }} />
      )}

      {/* Imagen */}
      <div style={{ flexShrink: 0, position: 'relative' }}>
        {cat.imageUrl
          ? <img src={cat.imageUrl} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 10 }} />
          : <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f1f3f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: 16 }}><FaImage /></div>}
        <label title="Cambiar imagen — cuadrada 500×500 px" style={{ position: 'absolute', bottom: -4, right: -4, width: 20, height: 20, borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: uploadingImage === cat.id ? 'wait' : 'pointer', border: '2px solid #fff', fontSize: 8 }}>
          {uploadingImage === cat.id ? <span className="spinner-border" style={{ width: 8, height: 8, borderWidth: 2 }} /> : <FaCamera style={{ color: '#4f46e5', fontSize: 8 }} />}
          <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) handleRowImageUpload(cat, f); }} style={{ display: 'none' }} disabled={uploadingImage === cat.id} />
        </label>
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{cat.name}{childCount > 0 && <span style={{ marginLeft: 6, fontSize: 11, color: '#6366f1', fontWeight: 600 }}>({childCount})</span>}</div>
        <div style={{ fontSize: 11, color: cat.active === false ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{depth ? 'Subcategoría · ' : ''}{cat.active === false ? 'Inactiva' : 'Activa'}</div>
      </div>

      {/* Acciones */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <button title="Editar" style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: 'none', background: '#dbeafe', color: '#2563eb', cursor: 'pointer', fontSize: 13 }} onClick={() => handleEditClick(cat)}><FaEdit /></button>
        <button title={cat.active === false ? 'Activar' : 'Desactivar'} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: cat.active === false ? '#f0fdf4' : '#fef2f2', color: cat.active === false ? '#16a34a' : '#dc2626' }} onClick={() => handleToggleActive(cat)} disabled={loadingToggle === cat.id}>{loadingToggle === cat.id ? '···' : cat.active === false ? '✓' : '✕'}</button>
      </div>
    </div>
  );

  return (
    <div className="container-fluid" style={{maxWidth:'100%',overflowX:'hidden'}}>
      {/* Header */}
      <div className="pg-head" style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <h2 style={{margin:0,fontWeight:700,fontSize:isMobile?'1.3rem':'1.8rem',color:'#1e293b'}}>Categorias</h2>
          <span style={{background:'#e2e8f0',color:'#475569',fontSize:11,padding:'2px 8px',borderRadius:10,fontWeight:600}}>{categories.length}</span>
        </div>
        <button onClick={() => setShowForm(!showForm)} title="Nueva categoria" type="button" style={{width:34,height:34,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:8,border:'none',background:'linear-gradient(135deg,#3b82f6,#2563eb)',color:'#fff',cursor:'pointer',fontSize:15,boxShadow:'0 2px 6px rgba(37,99,235,0.3)'}}>
          <FaPlus />
        </button>
      </div>

      {/* Formulario crear */}
      {showForm && (() => {
        const lbl: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'block' };
        return (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : 16 }}
          onClick={() => setShowForm(false)}
        >
          <form
            onSubmit={handleCreateCategory}
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', width: '100%', maxWidth: isMobile ? '100%' : 440, borderRadius: isMobile ? '20px 20px 0 0' : 16, maxHeight: isMobile ? 'calc(92vh / var(--ui-scale, 1))' : 'calc(90vh / var(--ui-scale, 1))', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }}>Nueva categoría</span>
              <button type="button" onClick={() => setShowForm(false)} style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTimes /></button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={lbl}>Nombre de la categoría</label>
                <input type="text" name="name" className="form-control" placeholder="Ej. Maquillaje" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required autoFocus style={{ height: 46, fontSize: 16, borderRadius: 10 }} />
              </div>

              <div>
                <label style={lbl}>Categoría padre <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span></label>
                <select className="form-control" value={form.parentId} onChange={e => setForm({ ...form, parentId: e.target.value })} style={{ height: 46, fontSize: 16, borderRadius: 10 }}>
                  <option value="">— Ninguna (categoría principal)</option>
                  {rootCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Elige una para crear una <strong>subcategoría</strong>, o déjala vacía para una categoría principal.</div>
              </div>

              <div>
                <label style={lbl}>Imagen <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span></label>
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, height: 130, borderRadius: 12, border: '2px dashed #cbd5e1', background: '#f8fafc', cursor: 'pointer', overflow: 'hidden' }}>
                  {form.imageUrl
                    ? <img src={form.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <><FaImage style={{ fontSize: 30, color: '#94a3b8' }} /><span style={{ fontSize: 13, color: '#64748b' }}>Toca para subir una imagen</span></>}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f && form.name) handleImageUpload(form.name, f, false); }} />
                </label>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                  Imagen <b>cuadrada de 500×500 px</b>. Se muestra en círculo pequeño en los filtros de la tienda,
                  así que centra el motivo y evita texto.
                </div>
                {!form.name && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Escribe el nombre antes de subir la imagen.</div>}
              </div>

              {formError && <div style={{ color: '#dc2626', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>{formError}</div>}
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '14px 18px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
              <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: '13px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" style={{ flex: 2, padding: '13px', borderRadius: 10, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>Crear categoría</button>
            </div>
          </form>
        </div>
        );
      })()}

      {/* Formulario editar */}
      {editForm && (() => {
        const lbl: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'block' };
        return (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : 16 }}
          onClick={() => !updating && setEditForm(null)}
        >
          <form
            onSubmit={handleUpdateCategory}
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', width: '100%', maxWidth: isMobile ? '100%' : 440, borderRadius: isMobile ? '20px 20px 0 0' : 16, maxHeight: isMobile ? 'calc(92vh / var(--ui-scale, 1))' : 'calc(90vh / var(--ui-scale, 1))', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 40px rgba(0,0,0,0.25)', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }}>Editar categoría</span>
              <button type="button" onClick={() => setEditForm(null)} disabled={updating} style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTimes /></button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={lbl}>Nombre</label>
                <input type="text" className="form-control" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Nombre de la categoría" style={{ height: 46, fontSize: 16, borderRadius: 10 }} />
              </div>
              <div>
                <label style={lbl}>Categoría padre</label>
                <select className="form-control" value={editForm.parentId || ''} disabled={hasChildren(editForm.id)} onChange={e => setEditForm({ ...editForm, parentId: e.target.value || null })} style={{ height: 46, fontSize: 16, borderRadius: 10, background: hasChildren(editForm.id) ? '#f1f5f9' : '#fff' }}>
                  <option value="">— Ninguna (categoría principal)</option>
                  {rootCategories.filter(c => c.id !== editForm.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {hasChildren(editForm.id) && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Esta categoría tiene subcategorías, por eso no puede volverse subcategoría de otra.</div>}
              </div>
              <div>
                <label style={lbl}>Imagen</label>
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, height: 130, borderRadius: 12, border: '2px dashed #cbd5e1', background: '#f8fafc', cursor: 'pointer', overflow: 'hidden' }}>
                  {editForm.imageUrl
                    ? <img src={editForm.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <><FaImage style={{ fontSize: 30, color: '#94a3b8' }} /><span style={{ fontSize: 13, color: '#64748b' }}>Toca para cambiar la imagen</span></>}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(editForm.name, f, true); }} />
                </label>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                  Imagen <b>cuadrada de 500×500 px</b>. Se muestra en círculo pequeño en los filtros de la tienda,
                  así que centra el motivo y evita texto.
                </div>
              </div>
              {editError && <div style={{ color: '#dc2626', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>{editError}</div>}
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '14px 18px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
              <button type="button" onClick={() => setEditForm(null)} disabled={updating} style={{ flex: 1, padding: '13px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button type="submit" disabled={updating} style={{ flex: 2, padding: '13px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: updating ? 0.7 : 1 }}>{updating ? 'Guardando…' : 'Guardar cambios'}</button>
            </div>
          </form>
        </div>
        );
      })()}

      {/* Lista de categorias (árbol colapsable: raíz → subcategorías) */}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {topLevel.map(cat => {
          const kids = childrenOf(cat.id);
          const open = expanded.has(cat.id);
          return (
            <React.Fragment key={cat.id}>
              {renderRow(cat, 0, kids.length, open)}
              {open && kids.map(ch => renderRow(ch, 1, 0, false))}
            </React.Fragment>
          );
        })}
        {categories.length === 0 && (
          <div style={{textAlign:'center',color:'#94a3b8',padding:'40px 0',fontSize:14}}>No hay categorias registradas</div>
        )}
      </div>
    </div>
  );
};

export default Categories;
